import {put, call} from 'redux-saga/effects';
import Group from "../../entities/group";
import * as Actions from "../actions/groupActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type GroupAction = {
    groupId: string;
    group: Group;
    groups: Group[];
};

export function* fetchOneGroup(action: GroupAction) {
	try {
		const group: Group = yield call(api.findOne, 'group', action.groupId);

		// Now we can update the user interface
		yield put({type: Actions.GROUP_FETCH_ONE_SUCCESS, groupId: action.groupId, group});
	}
	catch(err) {
		yield put({type: Actions.GROUP_FETCH_ONE_FAILURE, groupId: action.groupId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {GroupAction} action
 */
export function* saveGroup(action: GroupAction) {
    // console.debug('⮂ GROUP_SAVE saga', action.group?.id);
    
	if (action.group === undefined || action.group === null) {
		console.error('saveGroup received a null');
		throw new Error('saveGroup received a null');
	}

	if (!(action.group instanceof Group)) {
		action.group = new Group(action.group);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'group', action.group);
		if (addResult.isErr()) {
			console.error(`⮂ saveGroup: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.GROUP_SAVE_FAILURE, group: action.group, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.GROUP_SAVE, group: action.group});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'group', action.group.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.GROUP_SAVE_FAILURE, group: action.group, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {GroupAction} action
 * @param {Group[]} action.groups
 */
export function* saveManyGroups(action: GroupAction) {
    // console.debug('⮂ GROUP_SAVE_MANY saga', action.groups.map(group => group.id).join(','));
    
    if (!Array.isArray(action.groups)) {
        console.error('saveManyGroups expected an array, received', action.groups);
        throw new Error('saveManyGroups expected an array');
    }

	try {
		const toSave: Group[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.groups.length; i++) {
			const group = action.groups[i];
			group.persisted = false;
			toSave.push(group);
		}

		const addResult = yield call(idb.add, 'group', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyGroups: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.GROUP_SAVE_MANY_FAILURE, groups: action.groups, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.GROUP_SAVE_MANY, groups: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.GROUP_SAVE_MANY_FAILURE, groups: action.groups, status: 500, err});

		console.error(`Error saving ${action.groups.length} groups to database`, err);
	}
}

/**
 * @param {GroupAction} action
 */
export function* removeGroup(action: GroupAction) {
    const userId = getCurrentUserId();
	if (!userId || action.group === undefined || action.group === null || !(action.group instanceof Group)) {
			console.error('removeGroup expected type Group, received', action.group);
			throw new Error('removeGroup expected type Group');
		}

	try {
		// Mark as deleted
		action.group.status = 'D';
		action.group.addedOn = newHRTimestamp();
		action.group.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'group', action.group);
		if (addResult.isErr()) {
			console.error(`⮂ removeGroup: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.GROUP_REMOVE_FAILURE, group: action.group, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.GROUP_REMOVE, group: action.group});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.GROUP_REMOVE_FAILURE, group: action.group, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Group.
	*
	* @param {GroupAction} action
	*/
export function* restoreGroup(action: GroupAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreGroup expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.group === undefined || action.group === null) {
        console.error('restoreGroup expected a Group, received null');
        throw new Error('restoreGroup expected a Group, received null');
    }
    else if (!(action.group instanceof Group)) action.group = new Group(action.group); 
    
	try {
		// Mark as restored
		action.group.status = 'U';
		action.group.addedOn = newHRTimestamp();
		action.group.addedBy = userId;
		action.group.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'group', action.group);
		if (addResult.isErr()) {
			console.error(`⮂ restoreGroup: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.GROUP_RESTORE_FAILURE, group: action.group, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.GROUP_RESTORE, group: action.group});
	}
	catch(err) {
		yield put({type: Actions.GROUP_RESTORE_FAILURE, group: action.group, status: 500, err});

		console.error('Error restoring from database', err);
	}
}